import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';

export const RECOVERY_EXPORT_SCHEMA = 'app-guincho-checklist-recovery/v2';
export const RECOVERY_EXPORT_ALGORITHM = 'x25519-xsalsa20-poly1305-box';

export interface EncryptedRecoveryExport {
  schema: typeof RECOVERY_EXPORT_SCHEMA;
  algorithm: typeof RECOVERY_EXPORT_ALGORITHM;
  recipientKeyId: string;
  ephemeralPublicKey: string;
  nonce: string;
  ciphertext: string;
}

export interface RecoveryEncryptionResult {
  encrypted: EncryptedRecoveryExport;
}

export const encryptRecoveryJson = (
  value: unknown,
  recipientPublicKey: Uint8Array,
  ephemeralSecretKey: Uint8Array,
  nonce: Uint8Array,
  recipientKeyId: string
): RecoveryEncryptionResult => {
  if (recipientPublicKey.length !== nacl.box.publicKeyLength) {
    throw new Error('Chave publica de recuperacao invalida.');
  }
  if (ephemeralSecretKey.length !== nacl.box.secretKeyLength) {
    throw new Error('Chave efemera de recuperacao invalida.');
  }
  if (nonce.length !== nacl.box.nonceLength) {
    throw new Error('Nonce de recuperacao invalido.');
  }
  if (!recipientKeyId.trim()) {
    throw new Error('Identificador da chave de recuperacao ausente.');
  }

  const plaintext = naclUtil.decodeUTF8(JSON.stringify(value));
  const ephemeralKeyPair = nacl.box.keyPair.fromSecretKey(ephemeralSecretKey);
  const ciphertext = nacl.box(
    plaintext,
    nonce,
    recipientPublicKey,
    ephemeralKeyPair.secretKey
  );

  return {
    encrypted: {
      schema: RECOVERY_EXPORT_SCHEMA,
      algorithm: RECOVERY_EXPORT_ALGORITHM,
      recipientKeyId,
      ephemeralPublicKey: naclUtil.encodeBase64(ephemeralKeyPair.publicKey),
      nonce: naclUtil.encodeBase64(nonce),
      ciphertext: naclUtil.encodeBase64(ciphertext),
    },
  };
};

export const decryptRecoveryJson = (
  encrypted: EncryptedRecoveryExport,
  recipientSecretKey: string
): unknown => {
  if (
    encrypted.schema !== RECOVERY_EXPORT_SCHEMA ||
    encrypted.algorithm !== RECOVERY_EXPORT_ALGORITHM
  ) {
    throw new Error('Formato de recuperacao nao suportado.');
  }

  const secretKey = naclUtil.decodeBase64(recipientSecretKey);
  if (secretKey.length !== nacl.box.secretKeyLength) {
    throw new Error('Chave privada de recuperacao invalida.');
  }
  const ephemeralPublicKey = naclUtil.decodeBase64(encrypted.ephemeralPublicKey);
  const nonce = naclUtil.decodeBase64(encrypted.nonce);
  const ciphertext = naclUtil.decodeBase64(encrypted.ciphertext);
  const plaintext = nacl.box.open(
    ciphertext,
    nonce,
    ephemeralPublicKey,
    secretKey
  );

  if (!plaintext) {
    throw new Error('Chave privada incorreta ou arquivo corrompido.');
  }

  return JSON.parse(naclUtil.encodeUTF8(plaintext));
};
